<template>
    <div v-loading.fullscreen.lock="fullscreenLoading" class="advertisement-create">
        <div class="settings-field">
            <div class="basic-settings">
                <Name v-model:name="name" v-model:campaignApiId="advertisementApiId" label="Название объявления"
                    placeholder="Название объявления" :show-campaign-id-input="showAdvertisementApiIdInput" />
                <Period v-model:period="period" label="Период объявления" :min-date="campaignPeriodStart"
                    :max-date="campaignPeriodEnd" />
                <Type v-model:type="type" />

                <Comment v-model:comment="comment" placeholder="Комментарий к объявлению (необязательно)" />

                <!-- Кнопки для режима редактирования -->
                <div v-if="isEditMode" class="action-buttons">
                    <button @click="submitAdvertisement" class="submit submit--secondary">Обновить объявление</button>
                    <button v-if="advertisement && advertisement.status === 'pending_approval'"
                        @click="updateAndApprove" class="submit submit--primary">Обновить и отправить в API</button>
                </div>
                <!-- Кнопка для режима создания -->
                <button v-else @click="submitAdvertisement" class="submit">Сохранить объявление</button>
            </div>

            <div class="system-settings">
                <GoogleMap :frames="frames" :selectedCreatives="getSelectedCreatives()"
                    v-model:selectedFrameGids="selectedFrameGids" :disablePriceCheck="true"
                    :can-manage-test-frames="isAdmin" />
                <CreativesOption :creatives="filteredCreatives" v-model="selectedCreativeIds" />
                <WeekdayTimeSelector v-model="timeSettings" :allowed-time-intervals="campaignTimeSettings"
                    :active-periods="activePeriods" />
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

import Name from '../../common/Name.vue';
import Period from '../../common/Period.vue';
import Comment from '../../common/Comment.vue';
import GoogleMap from '../../common/GoogleMap.vue';
import CreativesOption from './CreativesOption.vue';
import Type from '../../common/Type.vue';
import WeekdayTimeSelector from './WeekdayTimeSelector.vue';
import { confirmAction } from '../../../composables/useConfirm';

export default {
    name: 'AdvertisementCreate',
    components: {
        Name,
        Period,
        Comment,
        GoogleMap,
        CreativesOption,
        Type,
        WeekdayTimeSelector,
    },
    props: {
        user: {
            type: Object,
            default: null,
        },
        oid: {
            type: String,
            default: null,
        },
        campaign: {
            type: Object,
            required: true,
        },
        creatives: {
            type: Array,
            default: () => [],
        },
        advertisement: {
            type: Object,
            default: null,
        },
        moderationMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            fullscreenLoading: false,
            campaignId: this.campaign.id,
            name: '',
            period: {
                start: this.campaign.period_start ? this.campaign.period_start.split(' ')[0] : '',
                end: this.campaign.period_end ? this.campaign.period_end.split(' ')[0] : ''
            },
            updated: Date.now(),
            status: 'approve',
            comment: '',
            type: 'picture',
            frames: this.campaign.frames ?? [],
            selectedCreativeIds: [],
            selectedFrameGids: [],
            advertisementFrames: [],
            timeSettings: [],
            advertisementApiId: '',
        };
    },
    computed: {
        isAdmin() {
            return !!(this.user && this.user.is_admin);
        },
        showAdvertisementApiIdInput() {
            return this.moderationMode || (this.isAdmin && this.isEditMode && this.advertisement?.status === 'pending_approval');
        },
        isEditMode() {
            return !!this.advertisement && !!this.advertisement.id;
        },
        filteredCreatives() {
            return (this.creatives || []).filter((c) => c.type === this.type);
        },
        campaignTimeSettings() {
            // Преобразуем timeSettings кампании в формат для компонента
            if (!this.campaign || !this.campaign.timeSettings) {
                return {};
            }

            // Определяем дни недели для периода объявления
            const adDaysOfWeek = this.getDaysOfWeekForPeriod();

            const result = {};
            Object.keys(this.campaign.timeSettings).forEach(day => {
                const dayNum = parseInt(day);
                // Включаем только дни, которые есть в периоде объявления
                if (adDaysOfWeek.includes(dayNum)) {
                    result[day] = this.campaign.timeSettings[day].map(interval => ({
                        day: dayNum,
                        startTime: interval.startTime,
                        endTime: interval.endTime,
                    }));
                }
            });

            return result;
        },
        campaignPeriods() {
            // Используем периоды из кампании, если они переданы
            if (this.campaign && this.campaign.periods && Array.isArray(this.campaign.periods) && this.campaign.periods.length > 0) {
                return this.campaign.periods;
            }

            // Если нет периодов в кампании, используем период кампании
            if (this.period.start && this.period.end) {
                return [{
                    start: this.period.start,
                    end: this.period.end,
                }];
            }

            return [];
        },
        campaignPeriodStart() {
            // Минимальная дата начала - дата начала кампании
            if (this.campaign && this.campaign.period_start) {
                return this.campaign.period_start.split(' ')[0];
            }
            return null;
        },
        campaignPeriodEnd() {
            // Максимальная дата окончания - дата окончания кампании
            if (this.campaign && this.campaign.period_end) {
                return this.campaign.period_end.split(' ')[0];
            }
            return null;
        },
        activePeriods() {
            // Период объявления для определения доступных дней
            if (this.period.start && this.period.end) {
                return [{
                    start: this.period.start,
                    end: this.period.end,
                }];
            }
            return [];
        },
    },
    mounted() {
        if (this.isEditMode) {
            this.$nextTick(() => {
                this.loadAdvertisementData();
            });
        }
    },
    watch: {
        campaign: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.frames) {
                    this.frames = newVal.frames;
                }
            },
        },
        advertisement: {
            deep: true,
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.id && this.isEditMode) {
                    this.$nextTick(() => {
                        this.loadAdvertisementData();
                    });
                }
            },
        },
        selectedFrameGids(newGids) {
            this.advertisementFrames = this.advertisementFrames.filter((frame) =>
                newGids.includes(frame.gid)
            );
        },
        type() {
            const allowedIds = new Set(this.filteredCreatives.map((c) => c.id));
            this.selectedCreativeIds = this.selectedCreativeIds.filter((id) => allowedIds.has(id));
        },
        period: {
            deep: true,
            handler() {
                // При изменении периода очищаем timeSettings, которые больше не доступны
                const allowedDays = this.getDaysOfWeekForPeriod();
                this.timeSettings = this.timeSettings.filter(ts => allowedDays.includes(ts.day));

                // Если после фильтрации нет настроек, но есть доступные дни, инициализируем
                if (this.timeSettings.length === 0 && allowedDays.length > 0) {
                    this.$nextTick(() => {
                        // Даем время на обновление campaignTimeSettings
                        setTimeout(() => {
                            this.initializeTimeSettingsForPeriod();
                        }, 100);
                    });
                }
            },
        },
    },

    methods: {
        initializeTimeSettingsForPeriod() {
            // Инициализируем timeSettings для периода на основе campaignTimeSettings
            const allowedDays = this.getDaysOfWeekForPeriod();
            const campaignSettings = this.campaignTimeSettings;

            allowedDays.forEach(day => {
                if (campaignSettings && campaignSettings[day] && campaignSettings[day].length > 0) {
                    // Добавляем все разрешенные интервалы для этого дня
                    campaignSettings[day].forEach(interval => {
                        this.timeSettings.push({
                            day: day,
                            startTime: interval.startTime,
                            endTime: interval.endTime,
                        });
                    });
                } else {
                    // Если нет ограничений, добавляем полные 24 часа
                    this.timeSettings.push({
                        day: day,
                        startTime: 0,
                        endTime: 24 * 60 * 60 * 1000, // 24 часа в ms
                    });
                }
            });
        },
        getDaysOfWeekForPeriod() {
            // Возвращает массив дней недели (1-7, где 1=понедельник, 7=воскресенье) для периода объявления
            if (!this.period.start || !this.period.end) {
                return [];
            }

            const daysOfWeek = [];
            const startDate = new Date(this.period.start);
            const endDate = new Date(this.period.end);

            // Проходим по всем дням периода
            const current = new Date(startDate);
            while (current <= endDate) {
                // Получаем день недели (1 = понедельник, 7 = воскресенье)
                const dayOfWeek = current.getDay() === 0 ? 7 : current.getDay();

                if (!daysOfWeek.includes(dayOfWeek)) {
                    daysOfWeek.push(dayOfWeek);
                }

                current.setDate(current.getDate() + 1);
            }

            return daysOfWeek;
        },
        getSelectedCreatives() {
            if (!this.selectedCreativeIds.length || !this.creatives.length) {
                return [];
            }
            return this.creatives.filter(c => this.selectedCreativeIds.includes(c.id));
        },
        loadAdvertisementData() {
            if (!this.advertisement) return;

            this.name = this.advertisement.name || '';
            this.campaignId = this.advertisement.campaignId || this.campaign.id;
            this.period = {
                start: this.advertisement.period?.start || '',
                end: this.advertisement.period?.end || '',
            };
            this.comment = this.advertisement.comment || '';
            this.status = this.advertisement.status || 'approve';
            this.type = this.advertisement.type || 'picture';

            // Преобразуем selectedFrameGids в строки для правильного сравнения в GoogleMap
            this.selectedFrameGids = (this.advertisement.selectedFrameGids || []).map(gid => String(gid));

            // Преобразуем selectedCreativeIds в числа
            this.selectedCreativeIds = (this.advertisement.selectedCreativeIds || []).map(id => Number(id));

            // Загружаем timeSettings
            this.timeSettings = Array.isArray(this.advertisement.timeSettings) && this.advertisement.timeSettings.length > 0
                ? this.advertisement.timeSettings.map(ts => ({
                    day: Number(ts.day),
                    startTime: Number(ts.startTime),
                    endTime: Number(ts.endTime),
                }))
                : [];

            // API ID для модерации
            this.advertisementApiId = this.advertisement.api_id ? String(this.advertisement.api_id) : '';
        },
        async submitAdvertisement() {
            this.fullscreenLoading = true;
            try {
                // Валидация основных полей
                if (!this.period.start || !this.period.end) {
                    this.$notify({
                        title: 'Ошибка валидации',
                        message: 'Выберите период объявления',
                        type: 'error',
                    });
                    this.fullscreenLoading = false;
                    return;
                }

                if (this.selectedFrameGids.length === 0) {
                    this.$notify({
                        title: 'Ошибка валидации',
                        message: 'Выберите хотя бы один экран',
                        type: 'error',
                    });
                    this.fullscreenLoading = false;
                    return;
                }

                if (this.selectedCreativeIds.length === 0) {
                    this.$notify({
                        title: 'Ошибка валидации',
                        message: 'Выберите хотя бы один креатив',
                        type: 'error',
                    });
                    this.fullscreenLoading = false;
                    return;
                }

                // Преобразуем timeSettings в правильный формат для API
                const timeSettingsForApi = (this.timeSettings || []).map(ts => ({
                    day: Number(ts.day),
                    startTime: Number(ts.startTime),
                    endTime: Number(ts.endTime),
                }));

                const formData = {
                    id: this.isEditMode ? this.advertisement.id : null,
                    campaignId: this.campaignId,
                    name: this.name,
                    status: this.status,
                    type: this.type,
                    period: this.period,
                    comment: this.comment,
                    creatives: this.selectedCreativeIds,
                    inventories: this.selectedFrameGids.map((gid) => ({ id: gid })),
                    timeSettings: timeSettingsForApi.length > 0 ? timeSettingsForApi : null,
                };

                // URL формируется на основе campaign из props
                let url;
                if (this.isEditMode) {
                    url = `/guaranteed/campaigns/${this.campaign.id}/advertisements/${this.advertisement.id}/edit`;
                } else if (this.campaign.campaign_external_id) {
                    // Для группы кампаний (CampaignExternal)
                    url = `/guaranteed/campaigns/external/${this.campaign.campaign_external_id}/advertisements/create`;
                } else {
                    url = `/guaranteed/campaigns/${this.campaign.id}/advertisements/create`;
                }

                await axios.post(url, formData);
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Выполнено!',
                    message: this.isEditMode ? 'Объявление успешно обновлено' : 'Объявление успешно создано',
                    type: 'success',
                });

                // После успешного сохранения перенаправляем на страницу кампаний
                setTimeout(() => {
                    window.location.href = '/guaranteed/campaigns';
                }, 1000);
            } catch (e) {
                this.fullscreenLoading = false;
                const errorMessage = e.response?.data?.error || 'Не удалось сохранить объявление';
                this.$notify({
                    title: 'Ошибка!',
                    message: errorMessage,
                    type: 'error',
                });
            } finally {
                this.fullscreenLoading = false;
            }
        },
        async updateAndApprove() {
            // Проверяем API ID
            if (!this.advertisementApiId || !this.advertisementApiId.trim()) {
                this.$notify({
                    title: 'Ошибка',
                    message: 'Необходимо указать API ID объявления',
                    type: 'error',
                });
                return;
            }

            const isConfirmed = await confirmAction({
                title: `Обновить объявление «${this.name || 'Без названия'}»?`,
                message: `Изменения будут отправлены в API с ID ${this.advertisementApiId}.`,
                confirmLabel: 'Обновить и отправить',
            });
            if (!isConfirmed) return;

            this.fullscreenLoading = true;
            try {
                // Сначала обновляем объявление (без перенаправления)
                // Преобразуем timeSettings в правильный формат для API
                const timeSettingsForApi = (this.timeSettings || []).map(ts => ({
                    day: Number(ts.day),
                    startTime: Number(ts.startTime),
                    endTime: Number(ts.endTime),
                }));

                const formData = {
                    id: this.advertisement.id,
                    campaignId: this.campaignId,
                    name: this.name,
                    status: this.status,
                    type: this.type,
                    period: this.period,
                    comment: this.comment,
                    creatives: this.selectedCreativeIds,
                    inventories: this.selectedFrameGids.map((gid) => ({ id: gid })),
                    timeSettings: timeSettingsForApi.length > 0 ? timeSettingsForApi : null,
                };

                const updateUrl = `/guaranteed/campaigns/${this.campaign.id}/advertisements/${this.advertisement.id}/edit`;
                await axios.post(updateUrl, formData);

                // Затем отправляем на approve
                const approveResponse = await axios.post(`/api/campaigns/${this.campaignId}/advertisements/${this.advertisement.id}/approve`, {
                    api_id: parseInt(this.advertisementApiId.trim())
                });

                this.$notify({
                    title: 'Успешно!',
                    message: 'Объявление обновлено и отправлено в API',
                    type: 'success',
                });

                // Перенаправляем обратно к списку кампаний
                setTimeout(() => {
                    window.location.href = '/guaranteed/campaigns';
                }, 1500);

            } catch (e) {
                const errorMessage = e.response?.data?.error || 'Не удалось обновить и отправить объявление';
                this.$notify({
                    title: 'Ошибка!',
                    message: errorMessage,
                    type: 'error',
                });
            } finally {
                this.fullscreenLoading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.advertisement-create {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    width: 100%;
    padding: 12px;

    .settings-field {
        display: flex;
        width: 100%;
        gap: 24px;
        align-items: flex-start;

        .basic-settings {
            flex: 0 0 320px;
            max-width: 360px;
            min-width: 260px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            border-right: 1px solid #e6e9ef;
            padding-right: 12px;

            button.submit {
                margin-top: 8px;
                width: 100%;
                padding: 10px 12px;
                border-radius: 10px;
                background: linear-gradient(90deg, rgba(0, 166, 255, 1), rgba(0, 120, 255, 0.9));
                color: #fff;
                font-weight: 600;
                border: none;
                cursor: pointer;
                transition: transform 0.12s ease, box-shadow 0.12s ease;
                box-shadow: 0 6px 18px rgba(0, 166, 255, 0.12);

                &:hover {
                    transition: transform 0.25s ease;
                    transform: translateY(-2px) scale(0.98);
                }

                &:active {
                    transform: translateY(0);
                    box-shadow: none;
                }
            }
        }

        .system-settings {
            flex: 1;
            display: flex;
            flex-direction: column;

            .tariffs {
                margin-top: 12px;
                display: flex;
                flex-direction: column;
                gap: 10px;

                .tariff-frame {
                    display: flex;
                    gap: 8px;
                    align-items: center;

                    .tariff-title {
                        font-weight: 600;
                        min-width: 220px;
                    }

                    select {
                        padding: 6px 8px;
                    }
                }
            }

            .action-buttons {
                display: flex;
                gap: 12px;
                margin-top: 8px;

                button.submit {
                    flex: 1;
                    padding: 10px 12px;
                    border-radius: 10px;

                    &--secondary {
                        background: #6c757d;
                        border-color: #6c757d;

                        &:hover {
                            background: #5a6268;
                            border-color: #545b62;
                        }
                    }

                    &--primary {
                        background: linear-gradient(90deg, rgba(40, 167, 69, 1), rgba(34, 197, 94, 0.9));
                        border-color: #28a745;

                        &:hover {
                            background: linear-gradient(90deg, rgba(34, 197, 94, 1), rgba(40, 167, 69, 0.9));
                            border-color: #218838;
                        }
                    }
                }
            }

        }
    }
}
</style>
